import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowRight, Clock3, FileText, UserCog, FileWarning } from "lucide-react";

/**
 * Combines: filings due in 60d + drivers with docs expiring in 60d
 * + open severe violations into a single actionable to-do list.
 */
export default function ActionItemsList() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [filingsRes, driversRes, violsRes] = await Promise.all([
          api.get("/filings"),
          api.get("/drivers/alerts").catch(() => ({ data: [] })),
          api.get("/violations").catch(() => ({ data: [] })),
        ]);
        const today = new Date();
        const in60 = new Date(); in60.setDate(today.getDate() + 60);
        const filings = (filingsRes.data || [])
          .filter((f) => f.expires_at && f.status !== "filed" && f.status !== "active" && f.status !== "archived")
          .map((f) => {
            const exp = new Date(f.expires_at);
            const days = Math.floor((exp - today) / (1000 * 60 * 60 * 24));
            return { ...f, days };
          })
          .filter((f) => f.days <= 60)
          .sort((a, b) => a.days - b.days)
          .slice(0, 4)
          .map((f) => ({
            kind: "filing",
            icon: FileText,
            title: f.label || f.type,
            sub: f.days < 0 ? `OVERDUE by ${Math.abs(f.days)} days` : f.days === 0 ? "Due today" : `Due in ${f.days} days`,
            urgent: f.days < 0,
            warn: f.days <= 30,
            link: "/filings",
            cta: "VIEW",
          }));
        const driverItems = (driversRes.data || []).slice(0, 4).map((d) => ({
          kind: "driver",
          icon: UserCog,
          title: d.driver_name,
          sub: `${d.doc_label} expires in ${d.days_left} days`,
          urgent: d.days_left < 0,
          warn: d.days_left <= 30,
          link: "/drivers",
          cta: "UPDATE",
        }));
        const violItems = (violsRes.data || [])
          .filter((v) => v.status === "open" && v.severity === "severe")
          .slice(0, 3)
          .map((v) => ({
            kind: "violation",
            icon: FileWarning,
            title: `Severe Violation · ${v.code}`,
            sub: v.description?.slice(0, 80),
            urgent: true,
            link: "/safety",
            cta: "REVIEW",
          }));
        const combined = [...violItems, ...filings, ...driverItems].slice(0, 8);
        if (alive) setItems(combined);
      } catch {
        if (alive) setItems([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (items === null) return null;

  return (
    <div className="hs-card p-0 overflow-hidden" data-testid="action-items-list">
      <div className="px-6 pt-5 pb-3 border-b border-slate-200 flex items-center justify-between">
        <div>
          <div className="hs-overline text-blue-700 inline-flex items-center gap-2"><Clock3 size={12}/> NEEDS YOUR ATTENTION</div>
          <h3 className="font-display font-bold text-xl mt-1">Action Items</h3>
        </div>
        <span className="hs-tag hs-tag-info" data-testid="action-items-count">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500" data-testid="action-items-empty">
          🎉 You're all caught up. No filings, drivers, or violations need attention right now.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((it, i) => {
            const Icon = it.icon;
            const tone = it.urgent ? "text-red-600" : it.warn ? "text-amber-600" : "text-slate-500";
            return (
              <li key={i}>
                <Link
                  to={it.link}
                  className="flex items-center gap-4 p-4 md:p-5 hover:bg-slate-50 transition-colors min-h-[72px] group"
                  data-testid={`action-item-${it.kind}-${i}`}
                >
                  <div className={`flex-shrink-0 ${tone}`}><Icon size={20} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">{it.title}</div>
                    <div className={`text-xs mt-0.5 ${tone}`}>{it.sub}</div>
                  </div>
                  <span className="hidden md:inline text-xs font-mono font-bold text-blue-700 group-hover:text-blue-900">{it.cta}</span>
                  <ArrowRight size={16} className="text-slate-400 group-hover:text-blue-700 flex-shrink-0" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
